import React, { useState, useMemo, useRef, useEffect } from "react";
import styles from "../assets/scss/common/TeamsSidebar.module.scss";
import { Link, useLocation } from "react-router-dom";
import { buildNavData } from "../helpers/buildNavData";
import ProfileDropdown from "../Components/Common/ProfileDropdown";
import LogoDark from "../assets/images/favicon.png";
import { usePermission } from "../context/PermissionContext";

/* ---------- Types ---------- */

interface SubChildMenuItem {
  formname: string;
  formurl: string;
  iconclass?: string;
}

interface ChildMenuItem {
  formname: string;
  formurl: string;
  iconclass?: string;
  lstsubchildform?: SubChildMenuItem[];
}

interface NavItem {
  formname: string;
  formurl: string;
  iconclass?: string;
  lstchildform?: ChildMenuItem[];
}

/* ---------- Component ---------- */

const VistaSideBar: React.FC = () => {
  const rawNavData = buildNavData() as NavItem[];
  const { hasPermission, isModuleVisible } = usePermission();
  const location = useLocation();

  /* ---- Permission-based filtering (recursive) ---- */
  const filterNavData = (items: any[]): any[] => {
    return items
      .filter((item) => {
        if (item.formname && !isModuleVisible(item.formname)) return false;
        if (item.module && !hasPermission(item.module)) return false;
        return true;
      })
      .map((item) => {
        const filteredItem = { ...item };
        if (item.lstchildform) filteredItem.lstchildform = filterNavData(item.lstchildform);
        if (item.lstsubchildform) filteredItem.lstsubchildform = filterNavData(item.lstsubchildform);
        return filteredItem;
      })
      .filter((item) => {
        const hasChildren =
          (item.lstchildform && item.lstchildform.length > 0) ||
          (item.lstsubchildform && item.lstsubchildform.length > 0);
        const hasSubmenuDefinition =
          item.lstchildform !== undefined || item.lstsubchildform !== undefined;
        if (hasSubmenuDefinition && !hasChildren) return false;
        if (!item.formurl && !hasChildren) return false;
        return true;
      });
  };

  const navData = useMemo(() => filterNavData(rawNavData), [rawNavData]);

  /* ---- App-panel state ---- */
  const [openAppPanel, setOpenAppPanel] = useState<boolean>(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  /* ---- Hover state for flyout menus ---- */
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  /* ---- Active parent detection ---- */
  const checkPathMatch = (formurl: string | undefined): boolean => {
    if (!formurl) return false;
    const path = location.pathname.toLowerCase();
    const target = formurl.toLowerCase();

    if (target === "/" || target === "/dashboard") {
      return path === target;
    }

    return path === target || path.startsWith(target + "/");
  };

  const getInitialActiveIndex = (): number =>
    navData.findIndex((item) => {
      // 1. Check parent url
      if (item.formurl && checkPathMatch(item.formurl)) return true;

      // 2. Check child and subchild urls
      if (item.lstchildform && item.lstchildform.length > 0) {
        return item.lstchildform.some((child: ChildMenuItem) => {
          if (child.formurl && checkPathMatch(child.formurl)) return true;
          if (child.lstsubchildform && child.lstsubchildform.length > 0) {
            return child.lstsubchildform.some(
              (subChild: SubChildMenuItem) => subChild.formurl && checkPathMatch(subChild.formurl)
            );
          }
          return false;
        });
      }

      return false;
    });

  const [active, setActive] = useState<number>(getInitialActiveIndex);

  // Sync active index state when location pathname changes
  useEffect(() => {
    setActive(getInitialActiveIndex());
  }, [location.pathname, navData]);

  /* ---- App panel items ---- */
  const appItems = [
    { name: "Zentix Docs", icon: "ri-file-text-line" },
    { name: "Zentix Saga", icon: "ri-bookmark-2-line" },
    { name: "Zentix Talent", icon: "ri-group-line" },
    { name: "Zentix Labs", icon: "ri-flask-line" },
    { name: "Zentix Sign", icon: "ri-pencil-line" },
    { name: "Zentix Sprint", icon: "ri-timer-line" },
    { name: "Zentix Track", icon: "ri-compass-3-line" },
    { name: "Zentix Eye", icon: "ri-eye-line" },
    { name: "Zentix Force", icon: "ri-shield-keyhole-line" },
  ];

  return (
    <div className={styles.sidebar_container}>
      <div className={styles.sidebar}>

        {/* ── Logo ── */}
        <div className={styles.logoArea}>
          <img src={LogoDark} alt="Zentixs" />
        </div>

        {/* ── Navigation rail ── */}
        <div className={`${styles.sidebar_menu} w-100`}>
          {navData.map((item, index) => {
            const isParentActive = item.lstchildform?.length
              ? item.lstchildform.some(
                  (child: ChildMenuItem) =>
                    location.pathname === child.formurl ||
                    child.lstsubchildform?.some(
                      (subChild) => location.pathname === subChild.formurl
                    )
                )
              : active === index || location.pathname === item.formurl;

            return (
              <div
                key={index}
                className={styles.menuWrapper}
                onMouseEnter={(e) => {
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current);
                    hoverTimeoutRef.current = null;
                  }
                  setHoverIndex(index);
                }}
                onMouseLeave={() => {
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current);
                  }
                  hoverTimeoutRef.current = setTimeout(() => {
                    setHoverIndex(null);
                    hoverTimeoutRef.current = null;
                  }, 200);
                }}
              >
                {/* Primary nav item */}
                {item.lstchildform?.length ? (
                  <div
                    className={`${styles.menuItem} ${isParentActive ? styles.active : ""}`}
                    title={item.formname}
                  >
                    <i className={`${item.iconclass} ${styles.icon}`} />
                    <div className={styles.tooltip}>{item.formname}</div>
                    <i className={`ri-arrow-right-s-line ${styles.arrow}`} />
                  </div>
                ) : (
                  <Link
                    to={item.formurl}
                    className={`${styles.menuItem} ${isParentActive ? styles.active : ""}`}
                    onClick={() => setActive(index)}
                    title={item.formname}
                  >
                    <i className={`${item.iconclass} ${styles.icon}`} />
                    <div className={styles.tooltip}>{item.formname}</div>
                  </Link>
                )}

                {/* ── Flyout submenu ── */}
                {hoverIndex === index && item.lstchildform?.length ? (
                  <div
                    className={styles.subMenu}
                    onMouseEnter={() => {
                      if (hoverTimeoutRef.current) {
                        clearTimeout(hoverTimeoutRef.current);
                        hoverTimeoutRef.current = null;
                      }
                    }}
                  >

                    <div className={styles.subMenuHeader}>
                      {item.iconclass && (
                        <i className={`${item.iconclass} ${styles.subMenuHeaderIcon}`} />
                      )}
                      <span className={styles.subMenuTitle}>{item.formname}</span>
                    </div>

                    <div className={styles.subMenuList}>
                      {item.lstchildform.map((child: ChildMenuItem, childIndex: number) => {
                        const hasSubChildren = child.lstsubchildform && child.lstsubchildform.length > 0;

                        if (hasSubChildren) {
                          return (
                            <div key={childIndex} className={styles.subMenuSection}>
                              {/* Static Section Header Label */}
                              <div className={styles.subMenuSectionHeader}>
                                {child.formname}
                              </div>
                              {/* Clickable Submenu section links */}
                              <div className={styles.subMenuSectionItems}>
                                {child.lstsubchildform?.map((subChild, subChildIndex) => (
                                  <Link
                                    key={subChildIndex}
                                    to={subChild.formurl}
                                    onClick={() => {
                                      setActive(-1);
                                      setHoverIndex(null); // Close sidebar on leaf node selection
                                    }}
                                    className={`${styles.subMenuItem} ${location.pathname === subChild.formurl ? styles.active : ""
                                      }`}
                                  >
                                    {subChild.iconclass && (
                                      <i className={`${subChild.iconclass} ${styles.icon}`} />
                                    )}
                                    <div className={styles.tooltip}>{subChild.formname}</div>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          );
                        } else {
                          // Standard flat clickable link
                          return (
                            <Link
                              key={childIndex}
                              to={child.formurl}
                              onClick={() => {
                                setActive(-1);
                                setHoverIndex(null); // Close sidebar drawer when clicking final leaf url
                              }}
                              className={`${styles.subMenuItem} ${location.pathname === child.formurl ? styles.active : ""
                                }`}
                            >
                              <i className={`${child.iconclass} ${styles.icon}`} />
                              <div className={styles.tooltip}>{child.formname}</div>
                            </Link>
                          );
                        }
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        {/* ── App launcher ── */}
        <div
          className={styles.appLauncher}
          onClick={() => setOpenAppPanel(!openAppPanel)}
          title="App Launcher"
        >
          <i className={openAppPanel ? "ri-close-line" : "ri-apps-2-line"} />
        </div>

        {/* ── Profile dropdown ── */}
        <div className="profilebottom border-top-sidebar">
          <ProfileDropdown />
        </div>

        {/* ── App panel overlay ── */}
        {openAppPanel && (
          <div
            className={styles.appPanelOverlay}
            onClick={() => setOpenAppPanel(false)}
          >
            <div
              className={styles.appPanel}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.appPanelTitle}>Applications</div>

              <input
                type="text"
                placeholder="Search apps…"
                className={styles.searchBox}
              />

              <div className={styles.appGrid}>
                {appItems.map((app, i) => (
                  <div key={i} className={styles.appItem}>
                    <i className={app.icon} />
                    <span>{app.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default VistaSideBar;
